import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { Perimeter } from '@atlaskit/atlassian-context/types';

import { CategoryStorageType, type ShouldEnforceKeyCallback } from '../../../types';
import { CacheLoader } from '../load-storage-controls-data';
import { Logger } from '../logger';
import { resetLogLevelCache } from '../logger/determine-log-level';
import type { PackageLogLevel } from '../logger/log-level';

import {
	__internalApplyInitialState,
	__internalGetAllowUncategorizedCookiesInOverridesGlobalState,
	__internalGetAllowUncategorizedItemsInIndexedDBOverridesGlobalState,
	__internalGetAllowUncategorizedItemsInWebStorageOverridesGlobalState,
	__internalGetDisableIndexedDbConsentChecksGlobalState,
	__internalGetDisableWebStorageConsentChecksGlobalState,
	__internalGetHasInitialized,
	__internalGetIndexedDbControlsEnabledGlobalState,
	__internalGetProductGlobalState,
	__internalGetShouldEnforceKeyGlobalState,
	__internalGetWebStorageControlsEnabledGlobalState,
} from './core';

export interface InitializeBscGlobalStateParams {
	// Common params
	analyticsEnabled?: boolean;
	product?: string;
	perimeter?: Perimeter;
	localConsentMode?: boolean;

	// InitializeControls only
	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	disableWebStorageConsentChecks?: boolean;
	packageLogLevel?: PackageLogLevel;
	/**
	 * **Scope: cookies AND web storage** (despite the name).
	 *
	 * When `true`, both the `document.cookie` setter override AND the web-storage
	 * `setItem` overrides allow uncategorized items to pass through.
	 *
	 * For web-storage-only behaviour, use
	 * `allowUncategorizedItemsInWebStorageOverrides` instead.
	 */
	allowUncategorizedCookiesInOverrides?: boolean;
	/**
	 * **Scope: web storage ONLY.**
	 *
	 * Web-storage-only counterpart to `allowUncategorizedCookiesInOverrides`. When
	 * `true` and `webStorageEnabled` is `true`, web-storage (`localStorage` /
	 * `sessionStorage`) `setItem` overrides will allow uncategorized items to pass
	 * through. Has **no effect on cookies** — the `document.cookie` setter
	 * override never reads this value.
	 */
	allowUncategorizedItemsInWebStorageOverrides?: boolean;
	/**
	 * **Scope: IndexedDB ONLY.**
	 *
	 * IDB-only counterpart to `allowUncategorizedItemsInWebStorageOverrides`. When
	 * `true` and `indexedDbEnabled` is `true`, IndexedDB `open()` overrides will
	 * allow uncategorized databases to pass through instead of being blocked.
	 * Has **no effect on cookies or web storage** — only the IndexedDB open
	 * override reads this value.
	 */
	allowUncategorizedItemsInIndexedDBOverrides?: boolean;
	disableIndexedDbConsentChecks?: boolean;
	__UNSAFE_shouldEnforceKeyForRollout?: ShouldEnforceKeyCallback;
	userId?: string;
	cloudId?: string;

	/**
	 * When true, bypasses the "set once" guard so that a subsequent call (e.g. from
	 * initializeControls) can overwrite parameters that were already defaulted by an
	 * earlier accidental call (e.g. from ConsentBannerImpl).
	 */
	overrideOnceParams?: boolean;
}

export const initializeGlobalState = ({
	webStorageEnabled,
	indexedDbEnabled,
	disableWebStorageConsentChecks,
	packageLogLevel,
	allowUncategorizedCookiesInOverrides,
	allowUncategorizedItemsInWebStorageOverrides,
	allowUncategorizedItemsInIndexedDBOverrides,
	disableIndexedDbConsentChecks,
	__UNSAFE_shouldEnforceKeyForRollout,
	localConsentMode = false,
	product = 'all',
	perimeter,
	analyticsEnabled = true,
	userId,
	cloudId,
	overrideOnceParams = false,
}: InitializeBscGlobalStateParams = {}): void => {
	Logger.debug('Initialize global state invoked');

	const productGlobalState = __internalGetProductGlobalState();

	// If the app provides a value for product only in one place,
	// we want that value to be sticky and to override the default if necessary
	if (
		(!productGlobalState || productGlobalState == null || productGlobalState === 'all') &&
		product
	) {
		__internalApplyInitialState({ productGlobalState: product });
	}

	// Treating webStorageEnabled, indexedDbEnabled, and allowUncategorizedCookiesInOverrides differently
	// because they are only passed through initializeControls and
	// we don't want it to be defaulted to false via a call from the ConsentBanner
	const webStorageControlsEnabledGlobalState = __internalGetWebStorageControlsEnabledGlobalState();
	if (webStorageEnabled !== undefined && webStorageControlsEnabledGlobalState === undefined) {
		__internalApplyInitialState({
			webStorageControlsEnabledGlobalState:
				webStorageEnabled &&
				!fg('platform_moonjelly_bsc_master_killswitch') &&
				!fg('platform_moonjelly_bsc_web_storage_killswitch'),
		});
	}

	const indexedDbControlsEnabledGlobalState = __internalGetIndexedDbControlsEnabledGlobalState();
	if (indexedDbEnabled !== undefined && indexedDbControlsEnabledGlobalState === undefined) {
		__internalApplyInitialState({
			indexedDbControlsEnabledGlobalState:
				indexedDbEnabled &&
				!fg('platform_moonjelly_bsc_master_killswitch') &&
				!fg('platform_moonjelly_bsc_web_storage_killswitch'),
		});
	}

	// `disableWebStorageConsentChecks` is sticky like the other controls flags above and
	// mirrors the behaviour of `disableIndexedDbConsentChecks`. When the caller does not
	// explicitly pass a value, the global state is left undefined and the public getter
	// (`getBscGlobalState`) reports `false` by default. It only takes effect when the
	// matching `webStorageEnabled` is `true` — see the consumer in `initializeControls`.
	const disableWebStorageConsentChecksGlobalState =
		__internalGetDisableWebStorageConsentChecksGlobalState();
	if (
		disableWebStorageConsentChecks !== undefined &&
		disableWebStorageConsentChecksGlobalState === undefined
	) {
		__internalApplyInitialState({
			disableWebStorageConsentChecksGlobalState: disableWebStorageConsentChecks,
		});
	}

	const allowUncategorizedCookiesInOverridesGlobalState =
		__internalGetAllowUncategorizedCookiesInOverridesGlobalState();
	if (
		allowUncategorizedCookiesInOverrides !== undefined &&
		allowUncategorizedCookiesInOverridesGlobalState === undefined
	) {
		__internalApplyInitialState({
			allowUncategorizedCookiesInOverridesGlobalState: allowUncategorizedCookiesInOverrides,
		});
	}

	// `allowUncategorizedItemsInWebStorageOverrides` is the web-storage-only counterpart to
	// `allowUncategorizedCookiesInOverrides`. It is sticky in the same way, and only takes
	// effect when web-storage controls are enabled. It does not affect cookies.
	const allowUncategorizedItemsInWebStorageOverridesGlobalState =
		__internalGetAllowUncategorizedItemsInWebStorageOverridesGlobalState();
	if (
		allowUncategorizedItemsInWebStorageOverrides !== undefined &&
		allowUncategorizedItemsInWebStorageOverridesGlobalState === undefined
	) {
		__internalApplyInitialState({
			allowUncategorizedItemsInWebStorageOverridesGlobalState:
				allowUncategorizedItemsInWebStorageOverrides,
		});
	}

	// `allowUncategorizedItemsInIndexedDBOverrides` is the IDB-only counterpart to
	// `allowUncategorizedItemsInWebStorageOverrides`. It is sticky in the same way, and only
	// takes effect when IndexedDB controls are enabled. It does not affect cookies or web storage.
	const allowUncategorizedItemsInIndexedDBOverridesGlobalState =
		__internalGetAllowUncategorizedItemsInIndexedDBOverridesGlobalState();
	if (
		allowUncategorizedItemsInIndexedDBOverrides !== undefined &&
		allowUncategorizedItemsInIndexedDBOverridesGlobalState === undefined
	) {
		__internalApplyInitialState({
			allowUncategorizedItemsInIndexedDBOverridesGlobalState:
				allowUncategorizedItemsInIndexedDBOverrides,
		});
	}

	const disableIndexedDbConsentChecksGlobalState =
		__internalGetDisableIndexedDbConsentChecksGlobalState();
	if (
		disableIndexedDbConsentChecks !== undefined &&
		disableIndexedDbConsentChecksGlobalState === undefined
	) {
		__internalApplyInitialState({
			disableIndexedDbConsentChecksGlobalState: disableIndexedDbConsentChecks,
		});
	}

	// Sticky on first set, mirroring the consent-check flags above.
	const __UNSAFE_shouldEnforceKeyForRolloutGlobalState = __internalGetShouldEnforceKeyGlobalState();
	if (
		__UNSAFE_shouldEnforceKeyForRollout !== undefined &&
		__UNSAFE_shouldEnforceKeyForRolloutGlobalState === undefined
	) {
		__internalApplyInitialState({
			__UNSAFE_shouldEnforceKeyForRolloutGlobalState: __UNSAFE_shouldEnforceKeyForRollout,
		});
	}

	// All other state can only be initialized once and is defaulted if not passed on the first initialization.
	// overrideOnceParams allows a subsequent call (e.g. from initializeControls) to bypass this guard
	// and re-apply these parameters, correcting values that were defaulted by an earlier accidental call
	// (e.g. from ConsentBannerImpl before initializeControls has run).
	if (__internalGetHasInitialized() && !overrideOnceParams) {
		return;
	}

	// Force local consent mode when ConsentHub is not enabled because ConsentHub is
	// not hydrated in the perimeter as of this comment
	const localConsentModeGlobalState =
		localConsentMode || !isFeatureEnabled('consent-platform', 'consenthub');

	const cookiesCacheLoader = new CacheLoader(CategoryStorageType.Cookies, perimeter);
	const localStorageCacheLoader = new CacheLoader(CategoryStorageType.LocalStorage, perimeter);
	const sessionStorageCacheLoader = new CacheLoader(CategoryStorageType.SessionStorage, perimeter);
	const indexedDBCacheLoader = new CacheLoader(CategoryStorageType.IndexedDB, perimeter);

	const updates: Parameters<typeof __internalApplyInitialState>[0] = {
		localConsentModeGlobalState,
		analyticsEnabledGlobalState: analyticsEnabled,
		perimeterGlobalState: perimeter,
		cookiesCacheLoader,
		localStorageCacheLoader,
		sessionStorageCacheLoader,
		indexedDBCacheLoader,
		packageLogLevelGlobalState: packageLogLevel,
		hasInitialized: true,
	};

	if (cloudId) {
		updates.cloudIdGlobalState = cloudId;
	}

	if (userId) {
		updates.userIdGlobalState = userId;
	}

	__internalApplyInitialState(updates);

	// Invalidate the resolved Logger level so any callers that triggered an
	// early `Logger.*` call before init pick up the new `packageLogLevel`.
	resetLogLevelCache();
};
